import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputLabel,
  TextField,
} from '@mui/material';
import React, { memo, useEffect } from 'react';
import { CloseIcon } from '~/assets/icons';
import { useFormik } from 'formik';
import { IAccount } from '~/utils/interface/account';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '~/utils/constants/common';
import { cast, handleApiError, notify } from '~/utils/helper';
import { BaseRow, BaseSelect, BaseUploadFile } from '~/components/common';
import { useTranslation } from 'react-i18next';
import { UploadFile } from '~/utils/types/common';
import { DEPARTMENT } from '~/utils/constants/account';
import { handleCreateAccount, handleEditAccount } from '~/thunks/account/accountThunk';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { accountActions } from '~/thunks/account/accountSlice';
import { handleGetCompanyRoleSelect } from '~/thunks/company-role/companyRoleThunk';

type EditAccountDialogProps = {
  open: boolean;
  setOpen?: (open: boolean, needRefreshData?: boolean) => void;
  setOpenDrawer?: (open: boolean) => void;
  mode?: string;
  title?: string;
  data?: IAccount;
  children?: React.ReactNode;
};

const initialValues: IAccount = {
  name: '',
  email: '',
  password: '',
  avatar: undefined,
  department: '',
  address: '',
  phone: '',
  roles: [],
};

const EditAccountDialog = (props: EditAccountDialogProps) => {
  //#region Destructuring Props
  const { mode = DIALOG_MODE_CREATE, data } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/account']);
  const dispatch = useAppDispatch();
  const form = useFormik({
    validateOnChange: false,
    initialValues,
    onSubmit: async (values, { setFieldError }) => {
      if (mode === DIALOG_MODE_CREATE) {
        try {
          await dispatch(handleCreateAccount(values)).unwrap();
          dispatch(accountActions.changeNeedRefreshData(true));
          notify('success', t('createSuccess', { ns: 'admin/common' }));
          handleSetOpen(false, true);
        } catch (e: unknown) {
          handleApiError(e, setFieldError);
        }
        return;
      }
      if (mode === DIALOG_MODE_EDIT) {
        try {
          await dispatch(handleEditAccount({ id: accountId, params: values })).unwrap();
          dispatch(accountActions.changeNeedRefreshData(true));
          notify('success', t('editSuccess', { ns: 'admin/common' }));
          handleSetOpen(false, true);
        } catch (e: unknown) {
          handleApiError(e, setFieldError);
        }
        return;
      }
    },
  });
  //#endregion Declare Hook

  //#region Selector
  const { accountId } = useAppSelector((state) => state.account);
  const { companyRoleSelects } = useAppSelector((state) => state.companyRole);
  //#endregion Selector

  //#region Declare State
  //#endregion Declare State

  //#region Implement Hook
  useEffect(() => {
    if (mode === DIALOG_MODE_EDIT) {
      form.setFieldValue('name', cast(data?.name));
      form.setFieldValue('email', cast(data?.email));
      form.setFieldValue('department', cast(data?.department));
      form.setFieldValue('title', cast(data?.title));
      form.setFieldValue('phone', cast(data?.phone));
      form.setFieldValue('address', cast(data?.address));
      form.setFieldValue('roleIds', cast(data?.roleIds));
      form.setFieldValue('avatar', data?.avatar);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, data]);

  useEffect(() => {
    dispatch(handleGetCompanyRoleSelect());
  }, [dispatch]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    if (mode === DIALOG_MODE_CREATE) {
      form.resetForm();
    }
    if (mode === DIALOG_MODE_EDIT) {
      form.resetForm();
    }
    if (props.setOpen) {
      props.setOpen(open, needRefreshData);
    }
    if (props.setOpenDrawer) {
      props.setOpenDrawer(open);
    }
  };

  const handleCancel = () => {
    handleSetOpen(false);
    form.resetForm();
  };
  //#endregion Handle Function

  return (
    <Dialog open={props.open} onClose={() => handleSetOpen(false)} fullWidth maxWidth='md'>
      <DialogTitle>
        {mode === DIALOG_MODE_CREATE ? t('addNewMember') : t('editUser')}
        <IconButton onClick={() => handleSetOpen(false)} className='absolute right-[10px] top-[10px]'>
          <CloseIcon className='w-[10px] h-[10px]' />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box component='form'>
          <BaseRow className='gap-[20px] mb-[20px]'>
            <Box className='w-full'>
              <InputLabel>{t('memberName')}</InputLabel>
              <TextField
                name='name'
                placeholder={t('memberName')}
                value={form.values?.name}
                onChange={form.handleChange}
                error={Boolean(form.errors?.name)}
                helperText={form.errors?.name}
              />
            </Box>
          </BaseRow>
          <BaseRow className='gap-[20px] mb-[20px]'>
            <Box className='w-full'>
              <InputLabel>{t('email')}</InputLabel>
              <TextField
                name='email'
                placeholder={t('email')}
                value={form.values?.email}
                onChange={form.handleChange}
                error={Boolean(form.errors?.email)}
                helperText={form.errors?.email}
              />
            </Box>
            <Box className='w-full'>
              <InputLabel>{t('password')}</InputLabel>
              <TextField
                name='password'
                type='password'
                placeholder={t('password')}
                value={form.values?.password}
                onChange={form.handleChange}
                error={Boolean(form.errors?.password)}
                helperText={form.errors?.password}
              />
            </Box>
          </BaseRow>
          <BaseRow className='gap-[20px] mb-[20px]'>
            <Box className='w-full'>
              <InputLabel>{t('department')}</InputLabel>
              <BaseSelect
                name='department'
                value={form.values?.department}
                options={DEPARTMENT}
                onChange={form.handleChange}
                error={Boolean(form.errors?.department)}
                helperText={form.errors?.department}
              ></BaseSelect>
            </Box>
            <Box className='w-full'>
              <InputLabel>{t('phoneNumber')}</InputLabel>
              <TextField
                name='phone'
                placeholder={t('phoneNumber')}
                value={form.values?.phone}
                onChange={form.handleChange}
                error={Boolean(form.errors?.phone)}
                helperText={form.errors?.phone}
              />
            </Box>
          </BaseRow>
          <BaseRow className='gap-[20px] mb-[20px]'>
            <Box className='w-full'>
              <InputLabel>{t('address')}</InputLabel>
              <TextField
                name='address'
                placeholder={t('address')}
                value={form.values?.address}
                onChange={form.handleChange}
                error={Boolean(form.errors?.address)}
                helperText={form.errors?.address}
              />
            </Box>
            <Box className='w-full'>
              <InputLabel>{t('roles')}</InputLabel>
              <BaseSelect
                name='roleIds'
                value={form.values?.roleIds ?? []}
                options={companyRoleSelects}
                onChange={form.handleChange}
                multiple={true}
                error={Boolean(form.errors?.roleIds)}
                helperText={form.errors?.roleIds}
              ></BaseSelect>
            </Box>
          </BaseRow>
          <BaseRow className='gap-[20px] mb-[20px]'>
            <Box className='w-full'>
              <InputLabel>{t('titles')}</InputLabel>
              <TextField
                name='title'
                placeholder={t('titles')}
                value={form.values?.title}
                onChange={form.handleChange}
                error={Boolean(form.errors?.title)}
                helperText={form.errors?.title}
              />
            </Box>
            <Box className='w-full'></Box>
          </BaseRow>
          <Box className='gap-[20px] mb-[20px]'>
            <InputLabel>{t('avatar')}</InputLabel>
            <BaseUploadFile
              uploadedFiles={typeof form.values?.avatar === 'string' ? [{ url: form.values?.avatar }] : undefined}
              onChange={(files: Array<UploadFile>) => {
                form.setFieldValue('avatar', files[0]?.file ?? '');
              }}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button color='secondary' variant='contained' onClick={() => handleCancel()}>
          {t('cancel', { ns: 'admin/common' })}
        </Button>
        <Button
          color='primary'
          variant='contained'
          onClick={() => {
            form.handleSubmit();
            if (props.setOpenDrawer) {
              props.setOpenDrawer(false);
            }
          }}
        >
          {t('save', { ns: 'admin/common' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(EditAccountDialog);
